/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { Star, CheckCircle2, X, Edit3, Trash2, Plus, Filter, Quote, Mail, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { reviewsApi, adminApi, formatApiError } from "@/lib/api";

const EVENT_TYPES = ["Mariage", "Anniversaire", "Entreprise", "Photobooth", "Baptême", "Communion", "Association", "Senior", "Autre"];
const STATUS_LABELS = { all: "Tous", pending: "À modérer", published: "Publiés", rejected: "Refusés" };

function Stars({ value }) {
  return (
    <span className="inline-flex items-center gap-0.5 text-[#DCB457]">
      {[1,2,3,4,5].map((n) => <Star key={n} size={12} strokeWidth={1.3} fill={n <= value ? "currentColor" : "none"} />)}
    </span>
  );
}

function ManualReviewRequestPanel() {
  const [email, setEmail] = React.useState("");
  const [prenom, setPrenom] = React.useState("");
  const [label, setLabel] = React.useState("");
  const [date, setDate] = React.useState("");
  const [sending, setSending] = React.useState(false);

  const send = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      await adminApi.requestReview({
        email: email.trim(),
        prenom: prenom.trim(),
        event_label: label.trim(),
        event_date: date,
      });
      toast.success(`Demande d'avis envoyée à ${email}.`);
      setEmail(""); setPrenom(""); setLabel(""); setDate("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSending(false); }
  };

  return (
    <section className="card-luxe p-5 md:p-6 mb-6" data-testid="manual-request-panel">
      <div className="flex items-center gap-2 mb-2">
        <p className="eyebrow inline-flex items-center gap-2">
          <Send size={12} strokeWidth={1.4} /> Demande manuelle
        </p>
      </div>
      <h2 className="font-serif-luxe text-2xl mt-1">Demander un avis à un ancien client.</h2>
      <p className="text-xs text-white/55 mt-2 max-w-2xl">
        Utile pour les prestations réalisées <strong>avant la mise en ligne du site</strong> ou pour tout client
        qui n'a pas encore été relancé automatiquement.
      </p>
      <form onSubmit={send} className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="manual-request-form">
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Email du client *</label>
          <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="mr-email" className="input-luxe mt-1 w-full" placeholder="client@example.com" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Prénom</label>
          <input type="text" value={prenom} onChange={(e) => setPrenom(e.target.value)} data-testid="mr-prenom" className="input-luxe mt-1 w-full" placeholder="Sarah" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Événement</label>
          <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="mr-label" className="input-luxe mt-1 w-full" placeholder="Mariage · Domaine du Loup" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Date événement</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="mr-date" className="input-luxe mt-1 w-full" />
        </div>
        <div className="md:col-span-2">
          <button type="submit" disabled={sending || !email.trim()} data-testid="mr-send" className="btn-gold py-2 px-5 text-sm disabled:opacity-60 inline-flex items-center gap-2">
            {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} strokeWidth={1.5} />}
            {sending ? "Envoi…" : "Envoyer la demande d'avis"}
          </button>
        </div>
      </form>
    </section>
  );
}


function ReviewRemindersPanel() {
  const [pending, setPending] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [sending, setSending] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const load = () => {
    setLoading(true);
    adminApi.listPendingReminders()
      .then(setPending)
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  React.useEffect(() => { load(); }, []);

  const trigger = async () => {
    setSending(true);
    try {
      const res = await adminApi.triggerReviewReminders();
      toast.success(`${res.sent} email(s) envoyé(s).`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="card-luxe p-5 md:p-6 mb-6" data-testid="reminders-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="eyebrow inline-flex items-center gap-2">
            <Mail size={12} strokeWidth={1.4} /> Rappel automatique
          </p>
          <h2 className="font-serif-luxe text-2xl mt-2">Demandes d'avis à envoyer.</h2>
          <p className="text-xs text-white/55 mt-2 max-w-lg">
            Le système envoie automatiquement un email aux clients <strong>15 jours après leur événement</strong> (cron toutes les 6h).
            Vous pouvez aussi déclencher le batch manuellement.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setOpen((v) => !v)} data-testid="reminders-toggle" className="btn-ghost-gold !py-2 !px-3 !text-[11px]">
            {open ? "Masquer" : `Voir (${pending.length})`}
          </button>
          <button onClick={trigger} disabled={sending || pending.length === 0} data-testid="reminders-trigger" className="btn-gold !py-2 !px-3 !text-[11px] inline-flex items-center gap-2 disabled:opacity-60">
            {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} strokeWidth={1.4} />}
            Envoyer maintenant
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-5">
          {loading ? (
            <p className="text-white/55 italic text-sm">Chargement…</p>
          ) : pending.length === 0 ? (
            <p className="text-white/55 italic text-sm">Aucun client éligible pour l'instant. Les rappels s'activent 15 jours après chaque événement.</p>
          ) : (
            <ul className="divide-y divide-[#DCB457]/10 border border-[#DCB457]/12">
              {pending.map((p) => (
                <li key={p.quote_id} data-testid={`reminder-row-${p.quote_id}`} className="px-4 py-3 text-sm flex flex-wrap items-center justify-between gap-3">
                  <span className="text-white/80">{p.nom || p.email}</span>
                  <span className="text-[11px] text-white/55">{p.type_evenement} · {p.date_evenement}</span>
                  <span className="text-[11px] text-[#DCB457] font-mono">{p.email}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

export default function AdminAvis() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");

  const load = () => {
    setLoading(true);
    reviewsApi.adminList(filter === "all" ? undefined : filter)
      .then(setItems)
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const publish = async (id) => {
    try {
      await reviewsApi.adminUpdate(id, { status: "published" });
      toast.success("Avis publié.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const reject = async (id) => {
    try {
      await reviewsApi.adminUpdate(id, { status: "rejected" });
      toast.success("Avis refusé.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement cet avis ?")) return;
    try {
      await reviewsApi.adminDelete(id);
      toast.success("Avis supprimé.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-avis">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Modération</p>
          <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">Avis <span className="italic gold-text">clients.</span></h1>
          <p className="text-white/60 text-sm mt-3 max-w-2xl">
            Modérez les avis soumis, ajoutez manuellement vos avis Google/Mariages.net. Les avis publiés alimentent automatiquement la page <code className="text-xs text-[#DCB457]">/avis</code> et le JSON-LD pour les étoiles Google.
          </p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="admin-avis-add" className="btn-gold inline-flex items-center gap-2">
          <Plus size={14} strokeWidth={1.4} /> Ajouter un avis
        </button>
      </header>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <Filter size={14} strokeWidth={1.4} className="text-[#DCB457]" />
        {Object.entries(STATUS_LABELS).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`admin-avis-filter-${k}`}
            className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 border transition ${filter === k ? "border-[#DCB457] text-[#DCB457] bg-[#DCB457]/8" : "border-white/12 text-white/55 hover:border-[#DCB457]/40"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {err && <p className="text-sm text-red-300 mb-4">{err}</p>}

      <ReviewRemindersPanel />
      <ManualReviewRequestPanel />

      <div className="space-y-3">
        {loading ? (
          <p className="text-white/55 italic">Chargement…</p>
        ) : items.length === 0 ? (
          <p className="text-white/55 italic">Aucun avis pour ce filtre.</p>
        ) : (
          items.map((r) => (
            <article key={r.id} data-testid={`admin-avis-row-${r.id}`} className="card-luxe p-5 md:p-6">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <Stars value={r.rating} />
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[#DCB457] border border-[#DCB457]/30 px-2 py-0.5">{r.event_type}</span>
                  {r.city && <span className="text-[11px] text-white/55">— {r.city}</span>}
                  {r.source !== "client_form" && (
                    <span className="text-[10px] uppercase tracking-[0.16em] text-white/45 border border-white/12 px-2 py-0.5">{r.source}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {r.status === "pending" && (
                    <>
                      <button onClick={() => publish(r.id)} data-testid={`admin-avis-publish-${r.id}`} className="text-[11px] inline-flex items-center gap-1 text-[#DCB457] border border-[#DCB457]/40 px-2.5 py-1.5 hover:bg-[#DCB457]/10">
                        <CheckCircle2 size={12} strokeWidth={1.4} /> Publier
                      </button>
                      <button onClick={() => reject(r.id)} data-testid={`admin-avis-reject-${r.id}`} className="text-[11px] inline-flex items-center gap-1 text-red-300 border border-red-400/30 px-2.5 py-1.5 hover:bg-red-500/10">
                        <X size={12} strokeWidth={1.4} /> Refuser
                      </button>
                    </>
                  )}
                  {r.status === "published" && (
                    <button onClick={() => reject(r.id)} data-testid={`admin-avis-unpublish-${r.id}`} className="text-[11px] inline-flex items-center gap-1 text-white/55 border border-white/15 px-2.5 py-1.5 hover:bg-white/5">
                      <X size={12} strokeWidth={1.4} /> Dépublier
                    </button>
                  )}
                  <button onClick={() => setEditing(r)} data-testid={`admin-avis-edit-${r.id}`} className="text-[11px] inline-flex items-center gap-1 text-white/65 border border-white/12 px-2.5 py-1.5 hover:border-[#DCB457]/40">
                    <Edit3 size={12} strokeWidth={1.4} /> Éditer
                  </button>
                  <button onClick={() => remove(r.id)} data-testid={`admin-avis-delete-${r.id}`} className="text-[11px] inline-flex items-center gap-1 text-red-300 border border-red-400/30 px-2.5 py-1.5 hover:bg-red-500/10">
                    <Trash2 size={12} strokeWidth={1.4} /> Suppr.
                  </button>
                </div>
              </div>
              {r.title && <h3 className="font-serif-luxe text-lg">{r.title}</h3>}
              <p className="text-sm text-white/75 leading-relaxed mt-2 flex items-start gap-2">
                <Quote size={14} strokeWidth={1.4} className="text-[#DCB457]/40 shrink-0 mt-0.5" />
                <span>{r.content}</span>
              </p>
              <p className="text-[11px] text-white/45 mt-3">— {r.author_name} · {r.event_date || "date n/c"}</p>
            </article>
          ))
        )}
      </div>

      {showAdd && <ReviewModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editing && <ReviewModal review={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ReviewModal({ review, onClose, onSaved }) {
  const isEdit = !!review;
  const [form, setForm] = useState(
    review
      ? { ...review }
      : { author_name: "", city: "", event_type: "Mariage", event_date: "", rating: 5, title: "", content: "", status: "published", source: "manual" }
  );
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        await reviewsApi.adminUpdate(review.id, form);
        toast.success("Avis mis à jour.");
      } else {
        await reviewsApi.adminCreate(form);
        toast.success("Avis ajouté.");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start md:items-center justify-center p-4 md:p-8 overflow-y-auto" data-testid="admin-avis-modal">
      <form onSubmit={submit} className="card-luxe p-6 md:p-8 max-w-2xl w-full space-y-4">
        <div className="flex items-start justify-between">
          <h3 className="font-serif-luxe text-2xl">{isEdit ? "Éditer" : "Ajouter"} un avis</h3>
          <button type="button" onClick={onClose} className="text-white/55 hover:text-[#DCB457]"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="input-luxe" placeholder="Prénom Nom *" required value={form.author_name} onChange={(e) => setForm({...form, author_name: e.target.value})} data-testid="modal-author" />
          <input className="input-luxe" placeholder="Ville" value={form.city || ""} onChange={(e) => setForm({...form, city: e.target.value})} data-testid="modal-city" />
          <select className="input-luxe" value={form.event_type} onChange={(e) => setForm({...form, event_type: e.target.value})} data-testid="modal-type">
            {EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
          <input type="date" className="input-luxe" value={form.event_date || ""} onChange={(e) => setForm({...form, event_date: e.target.value})} data-testid="modal-date" />
          <select className="input-luxe" value={form.rating} onChange={(e) => setForm({...form, rating: parseInt(e.target.value)})} data-testid="modal-rating">
            {[5,4,3,2,1].map((n) => <option key={n} value={n}>{n} étoile{n>1?"s":""}</option>)}
          </select>
          {!isEdit && (
            <select className="input-luxe" value={form.source || "manual"} onChange={(e) => setForm({...form, source: e.target.value})} data-testid="modal-source">
              <option value="manual">Source : Manuel</option>
              <option value="google">Source : Google</option>
              <option value="mariages_net">Source : Mariages.net</option>
            </select>
          )}
        </div>

        <input className="input-luxe" placeholder="Titre court (optionnel)" value={form.title || ""} onChange={(e) => setForm({...form, title: e.target.value})} maxLength={120} data-testid="modal-title" />

        <textarea
          rows={5}
          className="input-luxe resize-none"
          placeholder="Contenu de l'avis (20-2000 caractères) *"
          required
          value={form.content}
          onChange={(e) => setForm({...form, content: e.target.value})}
          minLength={20}
          maxLength={2000}
          data-testid="modal-content"
        />

        {isEdit && (
          <select className="input-luxe" value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} data-testid="modal-status">
            <option value="pending">Statut : En attente</option>
            <option value="published">Statut : Publié</option>
            <option value="rejected">Statut : Refusé</option>
          </select>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-ghost-gold">Annuler</button>
          <button type="submit" disabled={saving} data-testid="modal-save" className="btn-gold disabled:opacity-60">
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </form>
    </div>
  );
}
